import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { InformationCircleIcon, MinusCircleIcon, PlusCircleIcon } from '@heroicons/react/outline';
import { CREDIT_SALES_CONTACT, formatCreditDate, formatCredits, useMarketIntelligenceCreditCheck } from '@/features/campaigns/v2/credits';

const formatExpiryDate = (iso: string, locale: string) => {
  try {
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return formatCreditDate(iso, locale);
  }
};

export const MarketIntelligenceCreditBalance: FC = () => {
  const { t, i18n } = useTranslation('marketIntelligence');
  const { balance, available, reserved, isKnown, isFeatureUnavailable, isLoading, isOutOfCredits } = useMarketIntelligenceCreditCheck();

  if (isFeatureUnavailable || isLoading || !isKnown) {
    return null;
  }

  const isOut = isOutOfCredits;
  const firstLot = balance?.expirySchedule?.[0];
  const BadgeIcon = isOut ? MinusCircleIcon : PlusCircleIcon;

  return (
    <div className='flex flex-col gap-3' role='status' aria-live='polite'>
      <div className='flex flex-wrap items-center gap-3'>
        <div className='inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-3.5 py-1.5 text-brand-900'>
          <BadgeIcon className='size-4 shrink-0' aria-hidden />
          <span className='text-sm font-bold'>
            {t('credits.reportsAvailable', { count: available, formattedCount: formatCredits(available, i18n.language) })}
          </span>
        </div>

        {!isOut && firstLot ? (
          <p className='text-sm text-primary-500'>{t('credits.expiringOn', { date: formatExpiryDate(firstLot.expiresAt, i18n.language) })}</p>
        ) : null}
      </div>

      {reserved > 0 ? (
        <p className='text-xs text-primary-600'>{t('credits.inProgress', { count: reserved, formattedCount: formatCredits(reserved, i18n.language) })}</p>
      ) : null}

      {isOut ? (
        <div className='flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900'>
          <InformationCircleIcon className='mt-0.5 size-5 shrink-0 text-amber-700' aria-hidden />
          <p>
            {t('credits.outOfCredits', {
              email: CREDIT_SALES_CONTACT.email,
            })}
          </p>
        </div>
      ) : null}
    </div>
  );
};
